import { createFileRoute } from "@tanstack/react-router";
import { marketingHead } from "@/lib/marketing/seo";
import { breadcrumbs, webPageOf } from "@/lib/marketing/ppl-schema";
import { MLink } from "@/lib/marketing/m-link";
import { Reveal } from "@/components/marketing/reveal";
import { MarketingShell } from "@/components/marketing/site/marketing-shell";
import { IntakeHero } from "@/components/marketing/site/intake-hero";
import { DemoTakeaways } from "@/components/marketing/site/demo-takeaways";
import { FilmLightbox } from "@/components/marketing/site/film-lightbox";
import { FILMS_ON } from "@/lib/marketing/film";
import { DemoSchedulerPath } from "@/routes/v2/-components/demo-scheduler-path";
import { DemoRequestPath } from "@/routes/v2/-components/demo-request-path";

function MarketingDemo() {
  return (
    <MarketingShell>
      <IntakeHero
        id="demo"
        badge="Book a demo"
        title={
          <>
            See PEOPLE AI Workforce <span className="text-gradient-warm">in action</span>
          </>
        }
        lead="Thirty minutes with the people who built it and run it in their own communities. Pick a time yourself, or send a note and we'll come to you."
        bottom="pb-10"
        extra={
          <Reveal delay={160}>
            <p className="mt-4 text-sm text-muted-foreground">
              Not ready to talk to anyone?{" "}
              <MLink href="/experience">
                <span className="cursor-pointer text-primary" data-testid="link-experience-inline">
                  Run a med pass on sample data
                </span>
              </MLink>{" "}
              (no account, no form).
            </p>
          </Reveal>
        }
      />

      <DemoTakeaways />

      {/* THE TWO PATHS, SIDE BY SIDE AND THE SAME SIZE.
          One of them asks for nothing at all, so it does not sit under the one
          that asks for three fields. */}
      <section className="border-t border-border/60 bg-card/40">
        <div className="mx-auto max-w-6xl px-4 py-16 md:py-20">
          <Reveal>
            <div className="mx-auto max-w-2xl text-center">
              <p className="text-sm font-medium text-muted-foreground">Two ways in</p>
              <h2 className="font-display mt-3 text-3xl font-semibold tracking-tight md:text-4xl">
                Pick a time, or write to us
              </h2>
              <p className="mt-4 text-muted-foreground">
                Both reach the same people. Neither is the runner-up.
              </p>
              {/* The third way to meet the company before either: ninety seconds
                  of the workforce at its desks. */}
              {FILMS_ON ? (
                <div className="mt-6 flex justify-center">
                  <FilmLightbox surface="page" trigger="demo-two-ways" testId="button-demo-film" />
                </div>
              ) : null}
            </div>
          </Reveal>

          <div className="mx-auto mt-12 grid max-w-5xl items-start gap-6 lg:grid-cols-2">
            <DemoSchedulerPath />
            <DemoRequestPath />
          </div>

          <Reveal delay={140}>
            <p className="mx-auto mt-10 max-w-2xl text-center text-sm text-muted-foreground">
              Neither one yet? The interactive demo runs a morning med pass, a move-in, the lead
              pipeline and a month of invoices on sample data, in your browser, with{" "}
              <MLink href="/experience">
                <span
                  className="cursor-pointer font-medium text-foreground underline-offset-4 hover:underline"
                  data-testid="link-experience-demo"
                >
                  no account and nothing to fill in
                </span>
              </MLink>
              .
            </p>
          </Reveal>
        </div>
      </section>
    </MarketingShell>
  );
}

export const Route = createFileRoute("/v2/demo")({
  component: MarketingDemo,
  head: () => {
    const seo = {
      title: "Book a demo: 30 minutes on your own communities | PEOPLE AI Workforce",
      description:
        "Pick a 30-minute time with no form at all, or send a note and we'll come to you. You leave with a price for your operation, where your state stands, the eMAR opened in front of you, the BAA answer, what migration looks like and an operator you can call.",
      path: "/v2/demo",
      imageAlt: "PEOPLE AI Workforce. The operating system for assisted living communities",
    };
    return marketingHead({
      ...seo,
      schema: [webPageOf(seo), breadcrumbs({ name: "Book a demo", path: "/demo" })],
    });
  },
});
